import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Chip,
  ConfirmationModal,
  Table,
  TableColumnConfig,
  TableRowActions,
  Text,
  useModalControl,
} from '@frontend/design-system';
import { ParsedServiceDetails } from '../../api/services/transformers';
import { useServicesData } from '../../contexts/services-data-context';
import { ChipsList } from './chips-list';

type ServicesTableProps = {
  services: ParsedServiceDetails[];
  onEdit: (service: ParsedServiceDetails) => void;
  onDelete: (service: ParsedServiceDetails) => void;
};

export const ServicesTable = ({ services, onEdit, onDelete }: ServicesTableProps) => {
  const { t } = useTranslation('schedule-settings');
  const { modalProps: confirmDeleteModalProps, triggerProps: confirmDeleteTriggerProps } = useModalControl();
  const { refetchServicesData, isLoading } = useServicesData();

  const [selectedService, setSelectedService] = React.useState<ParsedServiceDetails | null>(null);

  const tableColumns = useMemo(
    (): TableColumnConfig<ParsedServiceDetails>[] => [
      {
        id: 'serviceName',
        Header: t('Service Name'),
        accessor: 'name',
        maxWidth: 300,
        sortType: 'caseInsensitive',
      },
      {
        id: 'duration',
        Header: t('Duration'),
        accessor: (row: ParsedServiceDetails) => (row.minutes ? `${row.minutes} ${t('Min')}` : '-'),
        disableSortBy: true,
        width: 100,
        maxWidth: 120,
      },
      {
        id: 'procedureCodes',
        Header: t('Procedure Codes'),
        accessor: (row: ParsedServiceDetails) => row.procedureCodes || [],
        cellRenderer: (codes: string[]) => {
          if (!codes?.length) {
            return <Text css={emptyTextStyles}>-</Text>;
          }

          const procedureCodeItems = codes.map((code) => ({ name: code }));

          return (
            <ChipsList
              items={procedureCodeItems}
              maxVisible={4}
              renderChip={({ name }) => (
                <Chip key={name} variant='neutral'>
                  {name}
                </Chip>
              )}
            />
          );
        },
        disableSortBy: true,
      },
    ],
    [t]
  );

  const tableActionMenu: TableRowActions<ParsedServiceDetails> = useMemo(() => {
    return {
      actions: [
        {
          Icon: () => <Icon name='edit-small' />,
          label: t('Edit'),
          onClick: (row) => onEdit(row),
        },
        {
          Icon: () => <Icon name='trash-small' />,
          label: t('Delete'),
          onClick: (row: ParsedServiceDetails) => {
            setSelectedService(row);
            confirmDeleteTriggerProps.onClick();
          },
        },
      ],
    };
  }, [onEdit, t, confirmDeleteTriggerProps]);

  const handleConfirmDelete = () => {
    if (selectedService) {
      onDelete(selectedService);
      setSelectedService(null);
      confirmDeleteModalProps.onClose();
    }
  };

  const handleCancelDelete = () => {
    setSelectedService(null);
    confirmDeleteModalProps.onClose();
  };

  return (
    <>
      <Table
        colConfig={tableColumns}
        data={services}
        rowActions={tableActionMenu}
        isLoading={isLoading}
        hasGlobalSearch
        tableActions={[
          {
            Icon: () => <Icon name='update' />,
            label: t('Refetch'),
            onClick: refetchServicesData,
            type: 'icon',
          },
        ]}
      />

      <ConfirmationModal
        title={t('Are you sure you want to delete {{serviceName}}?', {
          serviceName: selectedService?.name || '',
        })}
        message={t('This action cannot be undone.')}
        {...confirmDeleteModalProps}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmLabel={t('Delete')}
        cancelLabel={t('Cancel')}
        destructive
      />
    </>
  );
};

const emptyTextStyles = css`
  color: ${theme.colors.neutral40};
`;
